import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './UserProfile.css';
import FollowListModal from './FollowListModal';
import BannerAdmin from './BannerAdmin';

const API = process.env.REACT_APP_API_URL;

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleDateString('es-EC', { day: 'numeric', month: 'short' });
};

const UserProfile = ({ currentUser, refreshKey, onProfileUpdated, onViewProfile }) => {
  const { t } = useTranslation();
  const [profile, setProfile]   = useState(null);
  const [editing, setEditing]   = useState(false);
  const [form, setForm]         = useState({ full_name: '', bio: '', is_public: true });
  const [saving, setSaving]     = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [msg, setMsg]           = useState(null);
  const [error, setError]       = useState(null);
  const [myPosts, setMyPosts]   = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [followModal, setFollowModal] = useState(null); // 'followers' | 'following' | null
  const [mutedUsers, setMutedUsers]   = useState([]);
  const [showMuted, setShowMuted]     = useState(false);
  const fileRef = useRef(null);

  const fetchMuted = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/users/muted/`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
      setMutedUsers(r.data);
    } catch {}
  }, []);

  const handleUnmute = async (username) => {
    try {
      await axios.delete(`${API}/users/${username}/mute/`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
      setMutedUsers(prev => prev.filter(u => u.username !== username));
    } catch {}
  };

  const token = () => localStorage.getItem('access_token');

  const fetchMyPosts = useCallback(async (username) => {
    if (!username) return;
    setPostsLoading(true);
    try {
      const r = await axios.get(`${API}/users/${username}/qs/`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setMyPosts(r.data);
    } catch {
      // ignore
    } finally {
      setPostsLoading(false);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/profile/`, { headers: { Authorization: `Bearer ${token()}` } });
      setProfile(r.data);
      setForm({ full_name: r.data.full_name || '', bio: r.data.bio || '', is_public: r.data.is_public });
      fetchMyPosts(r.data.username);
    } catch {
      setError(t('profile_load_error'));
    }
  }, [fetchMyPosts, t]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile, refreshKey]);

  // Poll every 20s to pick up counter changes from others following/unfollowing
  useEffect(() => {
    const interval = setInterval(() => {
      axios.get(`${API}/profile/`, { headers: { Authorization: `Bearer ${token()}` } })
        .then(r => setProfile(r.data))
        .catch(() => {});
    }, 20000);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    setError(null);
    try {
      const r = await axios.patch(`${API}/profile/`, form, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setProfile(r.data);
      setEditing(false);
      setMsg(t('profile_updated'));
      if (onProfileUpdated) onProfileUpdated(r.data);
    } catch {
      setError(t('profile_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    setMsg(null);
    setError(null);
    const fd = new FormData();
    fd.append('photo', file);
    try {
      const r = await axios.post(`${API}/profile/photo/`, fd, {
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' },
      });
      setProfile(prev => ({ ...prev, photo_url: r.data.photo_url }));
      setMsg(t('photo_updated'));
      if (onProfileUpdated) onProfileUpdated({ photo_url: r.data.photo_url });
    } catch {
      setError(t('photo_upload_error'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  if (!profile) return <div className="loading">{t('loading_dots')}</div>;

  const initials = (profile.full_name || profile.username || '?')
    .split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="profile-container">
      <div className="profile-card">
        {/* Avatar */}
        <div className="profile-avatar-wrap">
          <div className="profile-avatar" onClick={() => fileRef.current?.click()}>
            {profile.photo_url
              ? <img src={profile.photo_url} alt="avatar" />
              : <span className="profile-initials">{initials}</span>
            }
            <div className="profile-avatar-overlay">
              {uploadingPhoto ? '...' : t('change_photo')}
            </div>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
          <div className="profile-badges">
            {profile.is_subscribed && <span className="badge badge--sub">{t('subscriber_badge')}</span>}
            {profile.can_upload_prices && <span className="badge badge--uploader">Uploader</span>}
            <span className={`badge ${profile.is_public ? 'badge--public' : 'badge--private'}`}>
              {profile.is_public ? t('public_profile') : t('private_profile')}
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="profile-info">
          <h2 className="profile-username">@{profile.username}</h2>
          {profile.email && <p className="profile-email">{profile.email}</p>}

          {editing ? (
            <div className="profile-edit-form">
              <label>
                {t('full_name_label')}
                <input
                  type="text"
                  value={form.full_name}
                  onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))}
                  placeholder={t('full_name_placeholder')}
                  maxLength={150}
                />
              </label>
              <label>
                {t('bio_label')}
                <textarea
                  value={form.bio}
                  onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                  placeholder={t('bio_placeholder')}
                  maxLength={500}
                  rows={3}
                />
                <span className="profile-char-count">{form.bio.length}/500</span>
              </label>
              <label className="profile-toggle-label">
                <span>{t('public_profile')}</span>
                <div
                  className={`profile-toggle ${form.is_public ? 'profile-toggle--on' : ''}`}
                  onClick={() => setForm(p => ({ ...p, is_public: !p.is_public }))}
                >
                  <div className="profile-toggle-knob" />
                </div>
              </label>
              <p className="profile-toggle-hint">
                {form.is_public ? t('public_profile_hint') : t('private_profile_hint')}
              </p>
              <div className="profile-edit-actions">
                <button className="profile-btn-save" onClick={handleSave} disabled={saving}>
                  {saving ? t('saving') : t('save_changes')}
                </button>
                <button className="profile-btn-cancel" onClick={() => setEditing(false)}>
                  {t('cancel')}
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-view">
              {profile.full_name && <p className="profile-fullname">{profile.full_name}</p>}
              <p className="profile-bio">{profile.bio || <em style={{ color: '#9ba5bc' }}>{t('no_bio')}</em>}</p>
              <button className="profile-btn-edit" onClick={() => { setEditing(true); setMsg(null); }}>
                {t('edit_profile')}
              </button>
            </div>
          )}

          {msg   && <div className="profile-success">{msg}</div>}
          {error && <div className="profile-error">{error}</div>}
        </div>
      </div>

      {/* Stats */}
      <div className="profile-stats">
        <div className="profile-stat">
          <span className="profile-stat-value">{profile.posts_count ?? '—'}</span>
          <span className="profile-stat-label">{t('my_qs')}</span>
        </div>
        <div
          className="profile-stat profile-stat--clickable"
          onClick={() => setFollowModal('followers')}
          title={t('see_followers')}
        >
          <span className="profile-stat-value">{profile.followers_count ?? '—'}</span>
          <span className="profile-stat-label">{t('followers')}</span>
        </div>
        <div
          className="profile-stat profile-stat--clickable"
          onClick={() => setFollowModal('following')}
          title={t('see_following')}
        >
          <span className="profile-stat-value">{profile.following_count ?? '—'}</span>
          <span className="profile-stat-label">{t('following')}</span>
        </div>
      </div>

      {followModal && (
        <FollowListModal
          username={profile.username}
          type={followModal}
          onClose={() => setFollowModal(null)}
          onViewProfile={onViewProfile}
        />
      )}

      {/* Usuarios silenciados */}
      <div className="profile-muted-section">
        <button
          className="profile-muted-toggle"
          onClick={() => { setShowMuted(s => !s); if (!showMuted) fetchMuted(); }}
        >
          🚫 {t('muted_users')}
          <span className="profile-muted-toggle-arrow">{showMuted ? '▲' : '▼'}</span>
        </button>
        {showMuted && (
          <div className="profile-muted-list">
            {mutedUsers.length === 0 ? (
              <p className="profile-muted-empty">{t('no_muted_users')}</p>
            ) : (
              mutedUsers.map(u => (
                <div key={u.username} className="profile-muted-item">
                  <div className="profile-muted-info">
                    {u.photo_url
                      ? <img src={u.photo_url} alt={u.username} className="profile-muted-avatar" />
                      : <div className="profile-muted-avatar-placeholder">{u.username[0].toUpperCase()}</div>
                    }
                    <div>
                      <span className="profile-muted-username">@{u.username}</span>
                      {u.full_name && <span className="profile-muted-name">{u.full_name}</span>}
                    </div>
                  </div>
                  <button
                    className="profile-muted-unmute"
                    onClick={() => handleUnmute(u.username)}
                  >{t('unmute')}</button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Banner admin — only for staff */}
      {currentUser?.is_staff && <BannerAdmin />}

      {/* My Q's */}
      <div className="profile-qs-section">
        <h3 className="profile-qs-title">{t('my_qs')}</h3>
        {postsLoading && <div className="loading" style={{ padding: '20px 0' }}>{t('loading_qs')}</div>}
        {!postsLoading && myPosts.length === 0 && (
          <div className="profile-qs-empty">{t('no_qs_published')}</div>
        )}
        {myPosts.map(post => (
          <div key={post.id} className="profile-qs-post">
            <div className="profile-qs-post-meta">
              <span className="profile-qs-post-time">{timeAgo(post.created_at)}</span>
              <button
                className="qs-delete-btn"
                onClick={async () => {
                  try {
                    await axios.delete(`${API}/qs/${post.id}/`, {
                      headers: { Authorization: `Bearer ${token()}` },
                    });
                    setMyPosts(prev => prev.filter(p => p.id !== post.id));
                    setProfile(prev => prev ? { ...prev, posts_count: (prev.posts_count || 1) - 1 } : prev);
                  } catch {}
                }}
                title={t('delete_q')}
              >✕</button>
            </div>
            <p className="profile-qs-post-text">{post.text}</p>
            <div className="profile-qs-post-stats">
              <span>♥ {post.likes_count || 0}</span>
              <span>💬 {post.comments_count || 0}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UserProfile;
